import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast.success("Köszönjük a feliratkozást", {
      description: "Új tételek és restock érkezésekor írunk először.",
    });
    setEmail("");
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[color:var(--he-ink)] text-[color:var(--he-bg)] pt-24 md:pt-32 pb-12 relative overflow-hidden"
    >
      {/* Decorative giant brand mark */}
      <div className="absolute -bottom-24 -left-10 hidden md:block opacity-[0.04] pointer-events-none select-none">
        <span className="font-serif-display italic text-[28rem] leading-none text-[color:var(--he-gold)]">
          Elixir
        </span>
      </div>

      <div className="relative he-container">
        {/* Newsletter strip */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-20 md:pb-24 border-b border-[color:var(--he-gold)]/30">
          <div className="md:col-span-6">
            <p className="overline-xs text-[color:var(--he-gold)] mb-6">Hírlevél</p>
            <h3 className="font-serif-display text-[2rem] md:text-[3rem] leading-[1.05] tracking-tight">
              Tudd meg elsőként, <br />
              <span className="italic text-[color:var(--he-gold)]">mikor érkezik új tétel.</span>
            </h3>
            <p className="mt-6 max-w-md text-[color:var(--he-bg)]/65 font-light text-sm leading-relaxed">
              Új kollekciókról, visszatérő készletről és időszakos
              kedvezményekről írunk — havonta legfeljebb egyszer-kétszer.
            </p>
          </div>
          <form
            onSubmit={subscribe}
            data-testid="newsletter-form"
            className="md:col-span-6 md:pl-10 flex flex-col justify-center"
          >
            <label className="overline-xs text-[color:var(--he-gold)] mb-3" htmlFor="newsletter-email">
              E-mail
            </label>
            <div className="flex items-center gap-4 border-b border-[color:var(--he-bg)]/30 focus-within:border-[color:var(--he-gold)] transition-colors duration-500">
              <input
                id="newsletter-email"
                type="email"
                required
                placeholder="email@cimed.hu"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email"
                className="flex-1 bg-transparent py-4 text-[color:var(--he-bg)] outline-none font-light placeholder:text-[color:var(--he-bg)]/35 placeholder:tracking-[0.18em] placeholder:uppercase placeholder:text-xs"
              />
              <button
                type="submit"
                data-testid="newsletter-submit"
                aria-label="Feliratkozás"
                className="w-11 h-11 rounded-full border border-[color:var(--he-gold)] flex items-center justify-center hover:bg-[color:var(--he-gold)] hover:text-[color:var(--he-ink)] transition-all duration-500"
              >
                <ArrowRight size={16} />
              </button>
            </div>
            <p className="mt-4 text-[10px] uppercase tracking-[0.28em] text-[color:var(--he-bg)]/40">
              Bármikor leiratkozhat. Nincs spam.
            </p>
          </form>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 py-20">
          <div className="md:col-span-5">
            <p
              className="font-serif-display text-5xl md:text-6xl tracking-wide text-[color:var(--he-gold)]"
              data-testid="footer-logo"
            >
              Hair <span className="italic">Elixir</span>
            </p>
            <p className="mt-8 max-w-sm text-[color:var(--he-bg)]/65 font-light leading-relaxed">
              Európai és kazahsztáni eredetű, prémium minőségű emberi haj.
              Magánszemélyeknek, magyar nyelvű ügyfélszolgálattal.
            </p>
            <div className="mt-10 flex gap-4">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                data-testid="ft-instagram"
                className="w-11 h-11 rounded-full border border-[color:var(--he-bg)]/25 hover:border-[color:var(--he-gold)] hover:bg-[color:var(--he-gold)] hover:text-[color:var(--he-ink)] flex items-center justify-center transition-all duration-500"
              >
                <Instagram size={15} />
              </a>
              <a
                href="mailto:hairelixirhungary@gmail.com"
                aria-label="E-mail"
                data-testid="ft-email"
                className="w-11 h-11 rounded-full border border-[color:var(--he-bg)]/25 hover:border-[color:var(--he-gold)] hover:bg-[color:var(--he-gold)] hover:text-[color:var(--he-ink)] flex items-center justify-center transition-all duration-500"
              >
                <Mail size={15} />
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="overline-xs text-[color:var(--he-gold)] mb-6">Kollekció</p>
            <ul className="space-y-3">
              <li>
                <Link to="/termekek" className="he-link text-sm" data-testid="ft-link-all">
                  Minden termék
                </Link>
              </li>
              <li>
                <Link to="/termekek?o=europai" className="he-link text-sm">
                  Európai
                </Link>
              </li>
              <li>
                <Link to="/termekek?o=kazah" className="he-link text-sm">
                  Kazahsztáni
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="overline-xs text-[color:var(--he-gold)] mb-6">Brand</p>
            <ul className="space-y-3">
              <li>
                <Link to="/filozofia" className="he-link text-sm">
                  Filozófia
                </Link>
              </li>
              <li>
                <Link to="/kerdoiv" className="he-link text-sm">
                  Hajtípus
                </Link>
              </li>
              <li>
                <Link to="/blog" className="he-link text-sm" data-testid="ft-link-blog">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/kapcsolat" className="he-link text-sm">
                  Kapcsolat
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="overline-xs text-[color:var(--he-gold)] mb-6">Kapcsolat</p>
            <p className="text-sm font-light leading-relaxed text-[color:var(--he-bg)]/80">
              hairelixirhungary@gmail.com
            </p>
            <p className="mt-4 text-sm font-light leading-relaxed text-[color:var(--he-bg)]/55">
              Hétfő–Péntek · 9–17 óra <br />
              Válasz 24 órán belül
            </p>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t border-[color:var(--he-bg)]/12">
          <div className="flex flex-col gap-1.5">
            <p className="overline-xs text-[color:var(--he-bg)]/50">
              © {new Date().getFullYear()} Hair Elixir — Minden jog fenntartva
            </p>
            <p className="overline-xs text-[color:var(--he-bg)]/35" data-testid="footer-imprint">
              Dócziné Bakró Hajnalka e.v. · Adószám 91672315-1-23
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link
              to="/adatvedelem"
              data-testid="ft-link-privacy"
              className="overline-xs text-[color:var(--he-bg)]/70 hover:text-[color:var(--he-gold)] transition-colors duration-300"
            >
              Adatvédelem
            </Link>
            <span className="overline-xs text-[color:var(--he-bg)]/25">·</span>
            <Link
              to="/cookie-tajekoztato"
              data-testid="ft-link-cookies"
              className="overline-xs text-[color:var(--he-bg)]/70 hover:text-[color:var(--he-gold)] transition-colors duration-300"
            >
              Cookie
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
